import {
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  styled,
  tableCellClasses,
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  useTheme,
  useMediaQuery,
  Fade,
  Tooltip,
} from '@mui/material';
import React, { useContext } from 'react';
import {
  DownloadRounded,
  RemoveRedEyeRounded,
  ModeEditOutlineRounded,
  Inventory,
  TrendingUp,
} from '@mui/icons-material';
import ShowComponent from '../ShowComponent';
import { Link } from 'react-router-dom';
import URLS from '../../services/urlConstants';
import { get } from '../../services/apiMethods';
import { downloadExcel } from '../../utils/utilities';
import { useNavigate } from 'react-router-dom';
import WarehouseContext from '../../context/WarehouseContext';
import ThemeModeContext from '../../context/ThemeModeContext';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: { xs: 11, sm: 12 },
    fontWeight: 500,
    padding: { xs: '2px 6px', sm: '4px 8px' },
    borderBottom: `1px solid ${
      theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'
    }`,
    minHeight: { xs: '28px', sm: '32px' },
    verticalAlign: 'middle',
  },
  [`&.${tableCellClasses.head}`]: {
    fontSize: { xs: 12, sm: 13 },
    fontWeight: 700,
    padding: { xs: '4px 6px', sm: '6px 8px' },
    color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.95)' : 'rgba(0,0,0,0.87)',
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)',
    backdropFilter: 'blur(8px)',
    boxShadow:
      theme.palette.mode === 'dark' ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.1)',
    minHeight: { xs: '32px', sm: '36px' },
    verticalAlign: 'middle',
    borderBottom: `2px solid #9c27b0`,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const DATA = [
  {
    _id: '1',
    name: 'DAAS',
    description: '1 headphone and 1 headset',
    count: 10,
  },
  {
    _id: '2',
    name: 'WAAS',
    description: '1 headphone and 1 headset',
    count: 20,
  },
];

function sumValues(data) {
  let sum = data.reduce((total, current) => total + current.count, 0);

  return sum;
}

export default function BundledStock({ data }) {
  const { selectedOwnerCode, selectedWarehouse, selectedOwnerGroup } = useContext(WarehouseContext);
  const { isDarkMode } = useContext(ThemeModeContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [downloadingReport, setDownloadingReport] = React.useState([]);
  const [downloadingAll, setDownloadingAll] = React.useState(false);

  const handleDownloadReport = async bundleType => {
    if (bundleType) {
      setDownloadingReport([...downloadingReport, bundleType]);
    } else {
      setDownloadingAll(true);
    }
    try {
      let filter = {};
      if (bundleType) {
        filter['bundleType'] = bundleType;
      }
      const { data } = await get(URLS.items.donwloadBundledStock, {
        params: {
          ...filter,
          warehouses: selectedWarehouse,
          ownerCodes: selectedOwnerCode,
          ownerGroups: selectedOwnerGroup,
        },
        responseType: 'blob',
      });
      downloadExcel(data, 'BUNDLED_STOCK.xlsx');
      if (bundleType) {
        setDownloadingReport(downloadingReport.filter(s => s === bundleType));
      } else {
        setDownloadingAll(false);
      }
    } catch (error) {
      console.log(error);
      if (bundleType) {
        setDownloadingReport(downloadingReport.filter(s => s === bundleType));
      } else {
        setDownloadingAll(false);
      }
    }
  };

  const handleView = async k => {
    try {
      navigate(`/edit/bundle/stock`, { state: { itemData: k } });
    } catch (error) {
      console.log(error);
    }
  };
  return (
    <Fade in timeout={500}>
      <Card
        elevation={0}
        sx={{
          border: `1px solid ${isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
          borderRadius: { xs: 2, sm: 2.5 },
          background: isDarkMode ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.8)',
          backdropFilter: 'blur(12px)',
          transition: 'all 0.2s ease',
        }}
      >
        <CardContent sx={{ p: 0 }}>
          {/* Header Section */}
          <Box
            sx={{
              p: { xs: 1, sm: 1.5 },
              borderBottom: `1px solid ${
                isDarkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'
              }`,
              background: 'linear-gradient(135deg, #9c27b012 0%, #9c27b006 100%)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
              <Box
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #9c27b0 0%, #8e24aa 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px #9c27b030',
                }}
              >
                <Inventory sx={{ fontSize: { xs: 16, sm: 18 }, color: 'white' }} />
              </Box>
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    color: '#9c27b0',
                    mb: 0.25,
                  }}
                >
                  Bundled Stock Inventory
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, display: 'block' }}
                >
                  Packaged item collections ready for deployment
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Table Content */}
          <TableContainer>
            <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  <StyledTableCell
                    align="center"
                    sx={{
                      width: { xs: '30%', sm: '25%' },
                      minWidth: { xs: '100px', sm: '120px' },
                      maxWidth: { xs: '100px', sm: '120px' },
                    }}
                  >
                    Bundle Name
                  </StyledTableCell>
                  <StyledTableCell
                    align="center"
                    sx={{
                      width: { xs: '35%', sm: '40%' },
                      minWidth: { xs: '120px', sm: '150px' },
                      maxWidth: { xs: '120px', sm: '150px' },
                    }}
                  >
                    Bundle Description
                  </StyledTableCell>
                  <StyledTableCell
                    align="center"
                    sx={{
                      width: { xs: '15%', sm: '15%' },
                      minWidth: { xs: '60px', sm: '70px' },
                      maxWidth: { xs: '60px', sm: '70px' },
                    }}
                  >
                    Quantity
                  </StyledTableCell>
                  <StyledTableCell
                    align="center"
                    sx={{
                      width: { xs: '20%', sm: '20%' },
                      minWidth: { xs: '80px', sm: '90px' },
                      maxWidth: { xs: '80px', sm: '90px' },
                    }}
                  >
                    Actions
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data?.map((d, k) => (
                  <StyledTableRow key={d._id}>
                    <StyledTableCell
                      align="center"
                      sx={{
                        width: { xs: '30%', sm: '25%' },
                        minWidth: { xs: '100px', sm: '120px' },
                        maxWidth: { xs: '100px', sm: '120px' },
                        padding: { xs: '2px 4px', sm: '4px 6px' },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 500,
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          lineHeight: 1.1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: '100%',
                          display: 'block',
                        }}
                        title={
                          selectedWarehouse?.length !== 1
                            ? `${d.name} - (${d.warehouseCode})`
                            : `${d.name}`
                        }
                      >
                        {selectedWarehouse?.length !== 1
                          ? `${d.name} - (${d.warehouseCode})`
                          : `${d.name}`}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{
                        width: { xs: '35%', sm: '40%' },
                        minWidth: { xs: '120px', sm: '150px' },
                        maxWidth: { xs: '120px', sm: '150px' },
                        padding: { xs: '2px 4px', sm: '4px 6px' },
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          lineHeight: 1.1,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          width: '100%',
                          display: 'block',
                        }}
                        title={d.description}
                      >
                        {d.description}
                      </Typography>
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{
                        width: { xs: '15%', sm: '15%' },
                        minWidth: { xs: '60px', sm: '70px' },
                        maxWidth: { xs: '60px', sm: '70px' },
                        padding: { xs: '2px 4px', sm: '4px 6px' },
                      }}
                    >
                      <Chip
                        label={d.count}
                        size="small"
                        variant="filled"
                        sx={{
                          fontWeight: 600,
                          fontSize: { xs: '0.6rem', sm: '0.65rem' },
                          height: { xs: 16, sm: 18 },
                          minWidth: { xs: '28px', sm: '32px' },
                          bgcolor: '#9c27b0',
                          color: 'white',
                          '& .MuiChip-label': {
                            px: { xs: 0.25, sm: 0.5 },
                            py: 0,
                          },
                        }}
                      />
                    </StyledTableCell>
                    <StyledTableCell
                      align="center"
                      sx={{
                        width: { xs: '20%', sm: '20%' },
                        minWidth: { xs: '80px', sm: '90px' },
                        maxWidth: { xs: '80px', sm: '90px' },
                        padding: { xs: '2px 4px', sm: '4px 6px' },
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          gap: { xs: 0.25, sm: 0.5 },
                          flexWrap: 'nowrap',
                          alignItems: 'center',
                        }}
                      >
                        <ShowComponent action={'view'} module="Inventory">
                          <IconButton
                            onClick={() => handleDownloadReport(d._id)}
                            disabled={!d.count || downloadingReport.includes(d._id)}
                            size="small"
                            sx={{
                              width: { xs: 20, sm: 24 },
                              height: { xs: 20, sm: 24 },
                              minWidth: { xs: 20, sm: 24 },
                              bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                              '&:hover': {
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
                                transform: 'translateY(-1px)',
                              },
                              transition: 'all 0.2s ease',
                              padding: 0,
                            }}
                          >
                            {downloadingReport.includes(d._id) ? (
                              <CircularProgress
                                size={{ xs: 10, sm: 12 }}
                                sx={{ color: '#9c27b0' }}
                              />
                            ) : (
                              <DownloadRounded
                                sx={{
                                  fontSize: { xs: 10, sm: 12 },
                                  color: '#9c27b0',
                                }}
                              />
                            )}
                          </IconButton>
                        </ShowComponent>
                        <ShowComponent action={'update'} module="Inventory">
                          <Tooltip title="View Details" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleView(d._id)}
                              sx={{
                                width: { xs: 20, sm: 24 },
                                height: { xs: 20, sm: 24 },
                                minWidth: { xs: 20, sm: 24 },
                                bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                                '&:hover': {
                                  bgcolor: isDarkMode
                                    ? 'rgba(255,255,255,0.15)'
                                    : 'rgba(0,0,0,0.08)',
                                  transform: 'translateY(-1px)',
                                },
                                transition: 'all 0.2s ease',
                                padding: 0,
                              }}
                            >
                              <RemoveRedEyeRounded
                                sx={{
                                  fontSize: { xs: 10, sm: 12 },
                                  color: '#9c27b0',
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </ShowComponent>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))}
              </TableBody>

              {/* Enhanced Footer */}
              <TableFooter>
                <StyledTableRow
                  sx={{
                    bgcolor: isDarkMode ? 'rgba(156, 39, 176, 0.1)' : 'rgba(156, 39, 176, 0.05)',
                    '&:hover': {
                      bgcolor: isDarkMode ? 'rgba(156, 39, 176, 0.15)' : 'rgba(156, 39, 176, 0.08)',
                    },
                  }}
                >
                  <StyledTableCell
                    sx={{
                      fontWeight: 700,
                      color: '#9c27b0',
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                    }}
                    colSpan={3}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 0.75 } }}>
                      <TrendingUp sx={{ fontSize: { xs: 14, sm: 16 } }} />
                      Total Bundled Stock
                    </Box>
                  </StyledTableCell>
                  <StyledTableCell align="right" sx={{ pr: 3 }}>
                    <Chip
                      label={sumValues(data)}
                      variant="filled"
                      sx={{
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.8rem' },
                        minWidth: { xs: '50px', sm: '60px' },
                        height: { xs: '28px', sm: '32px' },
                        bgcolor: '#9c27b0',
                        color: 'white',
                      }}
                    />
                  </StyledTableCell>
                </StyledTableRow>
                <StyledTableRow>
                  <StyledTableCell
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                    }}
                    colSpan={3}
                  >
                    Download Complete Report
                  </StyledTableCell>
                  <StyledTableCell align="right" sx={{ pr: 3 }}>
                    <ShowComponent action={'view'} module="Inventory">
                      <IconButton
                        onClick={() => handleDownloadReport()}
                        disabled={!sumValues(data) || downloadingAll}
                        sx={{
                          width: { xs: 32, sm: 36 },
                          height: { xs: 32, sm: 36 },
                          bgcolor: isDarkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                          border: `1px solid ${
                            isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'
                          }`,
                          '&:hover': {
                            bgcolor: isDarkMode ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.08)',
                            transform: 'translateY(-1px)',
                          },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {downloadingAll ? (
                          <CircularProgress size={{ xs: 14, sm: 16 }} sx={{ color: '#9c27b0' }} />
                        ) : (
                          <DownloadRounded
                            sx={{
                              fontSize: { xs: 14, sm: 16 },
                              color: '#9c27b0',
                            }}
                          />
                        )}
                      </IconButton>
                    </ShowComponent>
                  </StyledTableCell>
                </StyledTableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Fade>
  );
}
